// BankMarketSimulatorPage.js — Bank market simulator rendered inside BankAdminLayout.
// Input: destination market, market share, forward pricing, hedge toggle, time period.
// Results: P&L summary, monthly chart, sensitivity heatmap.

import React, { useState, useCallback } from 'react';
import api from '../../services/api';
import SimulationBanner from './SimulationBanner';
import BankPLTable from './BankPLTable';
import SensitivityHeatmap from './SensitivityHeatmap';
import BankLivePanel from './BankLivePanel';
import { TrendingUp, Shield, History, Radio } from 'lucide-react';

function BankInputForm({ params, setParams, onSubmit, loading }) {
  const handleChange = (key, value) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Destination Market */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Destination Market</label>
        <select
          value={params.destinationCountry}
          onChange={e => handleChange('destinationCountry', e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
        >
          <option value="Kenya">Kenya</option>
          <option disabled>Nigeria (Coming soon)</option>
          <option disabled>Ghana (Coming soon)</option>
        </select>
      </div>

      {/* Market Share */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Market Share (%) — <span className="text-gray-400">of investor flow</span>
        </label>
        <input
          type="number"
          min="0.1"
          max="100"
          step="0.5"
          value={params.marketSharePct}
          onChange={e => handleChange('marketSharePct', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
        />
      </div>

      {/* Forward Pricing */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Forward Pricing (bps over spot)
        </label>
        <input
          type="number"
          min="0"
          max="500"
          step="5"
          value={params.forwardPricingBps}
          onChange={e => handleChange('forwardPricingBps', parseFloat(e.target.value))}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
        />
        <p className="text-[10px] text-gray-400 mt-1">CIP reference ~30 bps | FF target ~50 bps</p>
      </div>

      {/* Hedge Toggle */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={params.hedgeEnabled}
            onChange={e => handleChange('hedgeEnabled', e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Hedge forward book</span>
        </label>
        <Shield className={`w-4 h-4 ${params.hedgeEnabled ? 'text-emerald-500' : 'text-gray-300'}`} />
      </div>

      {/* Time Period */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
          <input
            type="month"
            value={params.startDate?.slice(0, 7)}
            onChange={e => handleChange('startDate', e.target.value + '-01')}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
          <input
            type="month"
            value={params.endDate?.slice(0, 7)}
            onChange={e => handleChange('endDate', e.target.value + '-28')}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Synthetic Demand */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={params.syntheticDemandEnabled}
          onChange={e => handleChange('syntheticDemandEnabled', e.target.checked)}
          className="rounded border-gray-300"
        />
        <span className="text-sm text-gray-700">Include synthetic demand</span>
      </label>

      <button
        onClick={onSubmit}
        disabled={loading}
        className="w-full py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
      >
        {loading ? 'Running...' : 'Run Simulation'}
      </button>
    </div>
  );
}

export default function BankMarketSimulatorPage() {
  const [mode, setMode] = useState('historical'); // historical | live
  const [params, setParams] = useState({
    destinationCountry: 'Kenya',
    marketSharePct: 5,
    forwardPricingBps: 50,
    hedgeEnabled: true,
    startDate: '2024-01-01',
    endDate: '2024-12-28',
    syntheticDemandEnabled: true,
  });

  const [results, setResults] = useState(null);
  const [sensitivityGrid, setSensitivityGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pl');

  const runSimulation = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setSensitivityGrid(null);

    try {
      const [simResult, sensitivityResult] = await Promise.all([
        api.runBankSimulation(params),
        api.post('/simulator/bank/sensitivity', {
          hedgeCostBps: 30,
          revSharePct: 20,
          fxSpreadBps: 30,
          settlementFeeBps: 25,
          forwardRange: [20, 30, 40, 50, 60, 70, 80, 100],
          shareRange: [1, 3, 5, 10, 15, 20],
        }),
      ]);

      if (simResult.success) setResults(simResult.data);
      if (sensitivityResult.success) setSensitivityGrid(sensitivityResult.data);
    } catch (err) {
      setError(err.message || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [params]);

  const tabs = [
    { key: 'pl', label: 'P&L Breakdown' },
    { key: 'sensitivity', label: 'Sensitivity' },
    { key: 'positions', label: 'Positions' },
  ];

  return (
    <div>
      <SimulationBanner mode={mode} />

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Bank Market Simulator</h1>
          <p className="text-sm text-gray-500 mt-1">
            {mode === 'live'
              ? 'Track your bank P&L as real market data arrives.'
              : 'Model revenue from FX and forwards services for investor flow.'}
          </p>
        </div>
        <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5 text-xs">
          <button
            onClick={() => setMode('historical')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
              mode === 'historical' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            <History className="w-3.5 h-3.5" />
            Historical
          </button>
          <button
            onClick={() => setMode('live')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors ${
              mode === 'live' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            <Radio className="w-3.5 h-3.5" />
            Start Today
          </button>
        </div>
      </div>

      {mode === 'live' ? (
        <BankLivePanel />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Input panel */}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Parameters</h2>
              <BankInputForm
                params={params}
                setParams={setParams}
                onSubmit={runSimulation}
                loading={loading}
              />
            </div>
          </div>

          {/* Results panel */}
          <div className="lg:col-span-8 xl:col-span-9">
            <div className="bg-white rounded-xl border border-gray-100 p-5 min-h-[400px]">
              {error && (
                <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
              )}

              {!results && !loading && (
                <div className="text-center py-16 text-gray-400">
                  <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Configure parameters and run a simulation to see results.</p>
                </div>
              )}

              {loading && (
                <div className="flex items-center justify-center py-16 gap-2 text-sm text-gray-400">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                  Running bank simulation...
                </div>
              )}

              {results && (
                <>
                  {/* Tabs */}
                  <div className="border-b border-gray-200 mb-4">
                    <div className="flex gap-4">
                      {tabs.map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setActiveTab(tab.key)}
                          className={`pb-2 text-xs font-medium border-b-2 transition-colors ${
                            activeTab === tab.key
                              ? 'border-gray-900 text-gray-900'
                              : 'border-transparent text-gray-400 hover:text-gray-600'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {activeTab === 'pl' && (
                    <BankPLTable monthlyPL={results.monthlyPL} summary={results.summary} />
                  )}

                  {activeTab === 'sensitivity' && sensitivityGrid && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        Net Revenue by Forward Pricing vs Market Share
                      </h3>
                      <SensitivityHeatmap
                        grid={sensitivityGrid}
                        currentForwardBps={params.forwardPricingBps}
                        targetForwardBps={50}
                        currentSharePct={params.marketSharePct}
                      />
                    </div>
                  )}

                  {activeTab === 'positions' && (
                    <div className="overflow-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-2 text-gray-500">Month</th>
                            <th className="text-right py-2 px-2 text-gray-500">Positions</th>
                            <th className="text-right py-2 px-2 text-gray-500">Avg Spot</th>
                            <th className="text-right py-2 px-2 text-gray-500">FX Income</th>
                            <th className="text-right py-2 px-2 text-gray-500">Fwd Income</th>
                            <th className="text-right py-2 px-2 text-gray-500">Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.monthlyPL.map((m, idx) => (
                            <tr key={idx} className="border-b border-gray-50">
                              <td className="py-1.5 px-2 font-mono">{m.month}</td>
                              <td className="py-1.5 px-2 text-right">{m.position_count}</td>
                              <td className="py-1.5 px-2 text-right font-mono">
                                {m.avg_spot ? parseFloat(m.avg_spot).toFixed(2) : '-'}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono">
                                {parseFloat(m.fx_spread_income).toLocaleString()}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono">
                                {parseFloat(m.forward_premium_income).toLocaleString()}
                              </td>
                              <td className={`py-1.5 px-2 text-right font-mono font-medium ${
                                m.net_revenue >= 0 ? 'text-emerald-700' : 'text-red-700'
                              }`}>
                                {parseFloat(m.net_revenue).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
